import { type ReactNode } from "react";

/**
 * Die zweispaltige Flaeche der Anmeldung: links die Handlung, rechts die Vorfuehrung.
 *
 * Herausgezogen am 2026-08-14, weil die Warteseite (`janeway-auth/app/warteliste`)
 * dieselbe Optik braucht. Sie koennte sie nicht selbst bauen — im App-Code steht kein
 * `className` — und eine Kopie waere die naechste Doppelung, die beim ersten
 * Optik-Wechsel auseinanderlaeuft.
 *
 * **Das Raster steht ab jetzt an genau EINER Stelle.** `authAufteilungUnveraendert.test.tsx`
 * zaehlt es im gerenderten Markup und faellt bei zwei Vorkommen.
 *
 * WARUM 0.72fr links: die Handlung braucht nur so viel Breite, wie ein Formular oder ein
 * Absatz lesbar fuellt; der Rest gehoert dem Bild. Und WARUM die rechte Spalte erst ab
 * `lg` erscheint: auf dem Handy gehoert die ganze Flaeche dem, was der Nutzer tun soll.
 *
 * Server-renderbar: kein `"use client"`, kein State. Was hineingereicht wird, darf
 * beides sein.
 */
export function AuthSplitScreen({
  links,
  /** Ohne rechte Spalte bleibt ihre Huelle weg — kein leerer Kasten neben der Handlung. */
  rechts,
}: {
  links: ReactNode;
  rechts?: ReactNode;
}) {
  return (
    // Marke `KEKS_KOMPAKT_SEITE` (`CookieConsent`): die Hauptaktion steht hier
    // unten links, wo sonst die volle Fassung des Cookie-Hinweises laege.
    <div
      data-jw-keks-kompakt-seite=""
      className="lg:grid lg:min-h-[var(--jw-nutzhoehe)] lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1fr)]"
    >
      {/* `--jw-nutzhoehe` (`styles/globals.css`) und nicht `100svh`: der Body
          steht auf `--app-vh` und zieht Notch und Gestenleiste als Polster ab.
          Eine `100svh`-Spalte ragte um beide Raender darueber hinaus, und
          `overflow: hidden` schnitt sie unten ab (2026-10-06). `100vh` waere
          noch schlechter: auf iOS zaehlt es die Adressleiste mit.

          **Die Scrollhuelle gilt ohne Haltepunkt.** Bis 0.240.0 stand hier
          `min-h-[100svh]` plus `lg:h-[100svh] lg:overflow-y-auto` — die Spalte
          wuchs unter 1024px also mit ihrem Inhalt, in der Annahme, der Body
          scrolle. Er tut es nicht: `styles/globals.css` setzt unter 768px
          `html, body { position: fixed; overflow: hidden; inset: 0 }`. Bei
          360x640 lag der Zustimmungsbaustein des Assistenten damit bei y=672,
          „Konto erstellen" bei y=791 — beides ausserhalb des Bildes, und
          `scrollTo(0, 99999)` bewegte nichts. Auf einem 640px hohen Telefon war
          keine Registrierung moeglich und keine Kenntnisnahme der AGB (§ 305
          Abs. 2 BGB, § 5 DDG). Gebunden von
          `__tests__/anmeldemaske-scrollt-auf-dem-handy.test.tsx`. */}
      <div className="auth-scroll-fade flex h-[var(--jw-nutzhoehe)] flex-col overflow-y-auto px-5 pb-5 pt-24 sm:px-10 sm:pb-12 sm:pt-28 lg:px-14 lg:pb-12 lg:pt-24 xl:px-20">
        {links}
      </div>

      {rechts ? (
        <div className="relative hidden items-center justify-center overflow-hidden border-t border-border px-6 py-14 lg:flex lg:h-[var(--jw-nutzhoehe)] lg:items-stretch lg:border-l lg:border-t-0 lg:py-0">
          {rechts}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Das Punktraster hinter der Vorfuehrung — reine Atmosphaere.
 *
 * `aria-hidden` und `pointer-events-none`: es traegt keine Aussage und darf keine Klicks
 * abfangen, die dem Inhalt darueber gelten.
 */
export function AuthShowcaseBackdrop() {
  return (
    <div
      className="pointer-events-none absolute inset-0 text-foreground opacity-[0.035]"
      aria-hidden="true"
      style={{
        backgroundImage:
          "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(75% 65% at 50% 45%, #000 35%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(75% 65% at 50% 45%, #000 35%, transparent 100%)",
      }}
    />
  );
}
